import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { INotification, INotificationActionResponse, INotificationResponse } from '../../interfaces/notification.interface';
import { catchError, finalize, Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  #http: HttpClient = inject(HttpClient);
  #toastrService: ToastrService = inject(ToastrService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #apiBaseUrl: string = environment.apiUrl;

  #loading: WritableSignal<boolean> = signal(false);
  #notifications: WritableSignal<INotification[]> = signal([]);
  #unreadCount: WritableSignal<number> = signal(0);
  #lastUpdate: WritableSignal<Date> = signal(new Date());

  // Signals publics
  loading = this.#loading.asReadonly();
  notifications = this.#notifications.asReadonly();
  unreadCount = this.#unreadCount.asReadonly();
  lastUpdate = this.#lastUpdate.asReadonly();

  constructor() { }

  getAllNotifications(): Observable<INotificationResponse> {
    this.#loading.set(true);
    return this.#http.get<INotificationResponse>(`${this.#apiBaseUrl}/notifications/user`).pipe(
      tap((response: INotificationResponse) => {

        if (response.success && response.data) {
          const notifications = response.data.map(n => ({
            ...n,
            createdAt: new Date(n.createdAt)
          }));
          this.#notifications.set(notifications);
          this.#updateUnreadCount(notifications);
          this.#lastUpdate.set(new Date());
        }
      }),
      catchError(err => {
        console.error('Error fetching notifications:', err);
        if (err.status !== 404) { // 404 = aucune notification trouvée
          this.#toastrService.error('Erreur lors du chargement des notifications', 'Erreur');
        }
        throw err;
      }),
      finalize(() => this.#loading.set(false))
    );
  }

  getUnreadNotifications(): Observable<INotificationResponse> {
    return this.#http.get<INotificationResponse>(`${this.#apiBaseUrl}/notifications/unread`).pipe(
      tap((response: INotificationResponse) => {
        if (response.success && response.data) {
          this.#updateUnreadCount(response.data);
        }
      }),
      catchError(err => {
        console.error('Error fetching unread notifications:', err);
        throw err;
      })
    );
  }

  markAsRead(notificationId: string): Observable<INotificationActionResponse> {
    return this.#http.put<INotificationActionResponse>(
      `${this.#apiBaseUrl}/notifications/mark-read/${notificationId}`,
      {}
    ).pipe(
      tap((response: INotificationActionResponse) => {
        if (response.success) {
          this.#updateNotificationStatus(notificationId, true);
        }
      }),
      catchError(err => {
        console.error('Error marking notification as read:', err);
        this.#toastrService.error('Erreur lors de la mise à jour', 'Erreur');
        throw err;
      })
    );
  }

  markAsUnread(notificationId: string): Observable<INotificationActionResponse> {
    return this.#http.put<INotificationActionResponse>(
      `${this.#apiBaseUrl}/notifications/mark-unread/${notificationId}`,
      {}
    ).pipe(
      tap((response: INotificationActionResponse) => {
        if (response.success) {
          this.#updateNotificationStatus(notificationId, false);
        }
      }),
      catchError(err => {
        console.error('Error marking notification as unread:', err);
        this.#toastrService.error('Erreur lors de la mise à jour', 'Erreur');
        throw err;
      })
    );
  }

  addNotification(notification: INotification): void {
    const currentNotifications = this.#notifications();
    const updatedNotifications = [notification, ...currentNotifications];
    this.#notifications.set(updatedNotifications);
    this.#updateUnreadCount(updatedNotifications);

    // Afficher une notification toast
    this.#showNotificationToast(notification);
  }

  refresh(): void {
    this.getAllNotifications().pipe(
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe();
  }

  #updateNotificationStatus(notificationId: string, isRead: boolean): void {
    const notifications = this.#notifications().map(notification =>
      notification.id === notificationId
        ? { ...notification, isRead }
        : notification
    );
    this.#notifications.set(notifications);
    this.#updateUnreadCount(notifications);
  }

  #updateUnreadCount(notifications: INotification[]): void {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    this.#unreadCount.set(unreadCount);
  }

  #showNotificationToast(notification: INotification): void {
    const config = {
      timeOut: 8000,
      closeButton: true,
      progressBar: true,
      toastClass: 'toast'
    };

    switch (notification.type) {
      case 'reservation':
        this.#toastrService.success(notification.message, notification.title, config);
        break;
      case 'cancellation':
        this.#toastrService.warning(notification.message, notification.title, config);
        break;
      case 'payment':
        this.#toastrService.info(notification.message, notification.title, config);
        break;
      default:
        this.#toastrService.info(notification.message, notification.title, config);
    }
  }

  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

}
